/**
 * Jobs page tab bar — definitions + pending-count helpers
 * (admin sidebar redesign, owner addition Oct 18, 2026).
 *
 * The Jobs page carries three tabs whose ids are the EXISTING adminTab
 * ids, so deep links and the nav:change plumbing behave exactly as
 * before: 'jobs' (Job List), 'offers' (Offers console), 'invitations'.
 *
 * Each tab shows a pending-action bubble (solid attention pill) fed by
 * ONE aggregated count load in AdminJobsTabBar — the same payloads the
 * three pages already load for themselves:
 *  - Job List:    GET /api/admin/jobs         → count status 'pending_review'
 *  - Offers:      GET /api/admin/offers?status=issued&page=1&pageSize=1 → total
 *  - Invitations: GET /api/admin/invitations  → count status 'pending'
 */

export interface AdminJobsTabDef {
  /** Existing adminTab id this tab selects. */
  id: 'jobs' | 'offers' | 'invitations';
  label: string;
}

export const ADMIN_JOBS_TABS: AdminJobsTabDef[] = [
  { id: 'jobs', label: 'Job List' },
  { id: 'offers', label: 'Offers' },
  { id: 'invitations', label: 'Invitations' },
];

/**
 * Jobs awaiting admin approval. The jobs page's own "Needs Review"
 * quick-filter targets exactly this status, and the server transition
 * map is draft → pending_review → published. 'in_review' is NOT counted:
 * it is a live, publicly visible job that went back under review, not a
 * job awaiting its approval decision.
 */
export function countPendingJobs(jobs: Array<{ status?: string | null }> | null | undefined): number {
  if (!Array.isArray(jobs)) return 0;
  return jobs.filter((j) => j?.status === 'pending_review').length;
}

/**
 * Invitations still awaiting a response. Mirrors AdminInvitationsManager:
 * a missing status reads as 'pending', comparison is case-insensitive.
 */
export function countPendingInvitations(
  invitations: Array<{ status?: string | null }> | null | undefined,
): number {
  if (!Array.isArray(invitations)) return 0;
  return invitations.filter((i) => String(i?.status || 'pending').toLowerCase() === 'pending').length;
}
